import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  ReceiptData,
  ReceiptItem,
  AssistReceiptItem,
  ReferralReceiptItem
} from '../types';

interface ExportData {
  receiptData: ReceiptData;
  assistReceiptData: AssistReceiptItem[];
  referralReceiptData: ReferralReceiptItem[];
  currentDate: string;
}

interface UseExcelExporterReturn {
  exportToExcel: (data: ExportData, fileName?: string) => void;
  isExporting: boolean;
  error: string | null;
}

/**
 * 엑셀 파일 내보내기 훅
 * 업로드된 데이터를 원본 엑셀 형식으로 내보내기
 */
export const useExcelExporter = (): UseExcelExporterReturn => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 환자별 데이터를 원본 엑셀 형식으로 변환
   * @param receiptItems - 환자별 수납 데이터
   * @param currentDate - 현재 날짜
   */
  const convertToOriginalFormat = useCallback((receiptItems: ReceiptItem[], currentDate: string) => {
    return receiptItems.map((item, index) => {
      // 환자명에서 마스킹 해제 (원본 형태로 복원은 불가능하므로 현재 상태 유지)
      const patientName = item.name;

      // 총 진료비 계산 (실제 수납액 + 미수금)
      const totalFee = item.amount + (item.uncollectedAmount || 0);

      // 원본 엑셀 컬럼 형식으로 데이터 매핑
      return {
        '번호': index + 1,
        '환자': patientName,
        '차트번호': item.chartNumber,
        '어시스트': item.assistName,
        '진료의사': item.doctorName || '',
        '수납자': item.cashierName || '',
        '고객구분': '재진', // 기본값 (원본에서는 다양했지만 복원 불가)
        '내원경로': '기존환자', // 기본값
        '진료일': item.receiptDate || currentDate,
        '총진료비': totalFee,
        '청구액': item.insuranceClaim || 0,
        '본인부담금': totalFee - (item.insuranceClaim || 0),
        '비급여': 0, // 계산 복잡성으로 기본값 사용
        '부가가치세': item.valueAdded || 0,
        '할인액': 0, // 원본 데이터에서 복원 어려움
        '현금수납': item.cashAmount || 0,
        '카드수납': item.cardAmount || 0,
        '기타(온라인)': item.transferAmount || 0,
        '미수(+)선수(-)': item.uncollectedAmount || 0,
        '건강생활유지비승인': item.healthLifeSupport || 0
      };
    });
  }, []);

  /**
   * 어시스트별 집계 시트 생성
   * @param assistReceiptData - 어시스트별 집계 데이터
   */
  const createAssistSummaryData = useCallback((assistReceiptData: AssistReceiptItem[]) => {
    return assistReceiptData.map((item, index) => ({
      '번호': index + 1,
      '어시스트': item.assistName,
      '진료의사': item.doctorName || '',
      '수납자': item.cashierName || '',
      '진료구분': item.treatmentType,
      '건수': item.count,
      '금액': item.amount,
      '날짜': item.receiptDate
    }));
  }, []);

  /**
   * 내원경로별 집계 시트 생성
   * @param referralReceiptData - 내원경로별 집계 데이터
   */
  const createReferralSummaryData = useCallback((referralReceiptData: ReferralReceiptItem[]) => {
    return referralReceiptData.map((item, index) => ({
      '번호': index + 1,
      '내원경로': item.referralPath,
      '담당어시스트': item.assistName,
      '건수': item.count,
      '금액': item.amount,
      '날짜': item.receiptDate
    }));
  }, []);

  /**
   * 엑셀 파일로 내보내기
   * @param data - 내보낼 데이터
   * @param fileName - 파일명 (옵션)
   */
  const exportToExcel = useCallback(async (data: ExportData, fileName?: string) => {
    setIsExporting(true);
    setError(null);

    try {
      // 새 워크북 생성
      const workbook = XLSX.utils.book_new();

      // 1. 환자별 상세 데이터 시트
      const detailData = convertToOriginalFormat(data.receiptData.items, data.currentDate);
      if (detailData.length > 0) {
        const detailWorksheet = XLSX.utils.json_to_sheet(detailData);
        XLSX.utils.book_append_sheet(workbook, detailWorksheet, '환자별상세');
      }

      // 2. 어시스트별 집계 시트
      const assistSummaryData = createAssistSummaryData(data.assistReceiptData);
      if (assistSummaryData.length > 0) {
        const assistWorksheet = XLSX.utils.json_to_sheet(assistSummaryData);
        XLSX.utils.book_append_sheet(workbook, assistWorksheet, '어시스트별집계');
      }

      // 3. 내원경로별 집계 시트
      const referralSummaryData = createReferralSummaryData(data.referralReceiptData);
      if (referralSummaryData.length > 0) {
        const referralWorksheet = XLSX.utils.json_to_sheet(referralSummaryData);
        XLSX.utils.book_append_sheet(workbook, referralWorksheet, '내원경로별집계');
      }

      // 4. 요약 시트 생성
      const summaryData = [
        { '항목': '전체 환자 수', '값': data.receiptData.items.length },
        { '항목': '총 수납액', '값': data.receiptData.total },
        { '항목': '어시스트 집계 건수', '값': data.assistReceiptData.length },
        { '항목': '내원경로 집계 건수', '값': data.referralReceiptData.length },
        { '항목': '생성일시', '값': new Date().toLocaleString() }
      ];

      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, '요약');

      // 파일명 생성 (YYYY-MM-DD진료비통계.xlsx 형식)
      const finalFileName = fileName || `${data.currentDate}진료비통계.xlsx`;

      // 파일 다운로드
      XLSX.writeFile(workbook, finalFileName);

      console.log('✅ [Excel Export] 엑셀 파일 다운로드 완료:', finalFileName);

    } catch (exportError) {
      const errorMessage = exportError instanceof Error ? exportError.message : '엑셀 내보내기 중 오류가 발생했습니다';
      setError(errorMessage);
      console.error('❌ [Excel Export] 엑셀 내보내기 오류:', exportError);
      throw new Error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  }, [convertToOriginalFormat, createAssistSummaryData, createReferralSummaryData]);

  return {
    exportToExcel,
    isExporting,
    error
  };
};

// Default export
export default useExcelExporter;